/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import cn from "classnames";
import FilterIcon from "components/icons/common/FilterIcon";
import { Button } from "components/ui/Button";
import Heading from "components/ui/Heading";
import SearchBar from "components/ui/SearchBar";
import Stack from "components/ui/Stack";

interface ActionTableFrameProps {
  onFilter?: (...args: any) => void;
  onSearch?: (...args: any) => void;
  isSearching?: boolean;
  isFiltering?: boolean;
  children: React.ReactNode;
  searchArgs?: any;
  headerSlot?: React.ReactNode;
  title?: string | React.ReactNode;
  disabled?: boolean;
  className?: string;
  headerClassName?: string;
  searchWithSpecialCharacters?: boolean;
  placeholder?: string;
  searchInputClassName?: string;
}

const ActionTableFrame = ({
  onFilter,
  onSearch,
  children,
  isFiltering,
  isSearching,
  searchArgs,
  headerSlot,
  disabled,
  title,
  className,
  headerClassName,
  searchWithSpecialCharacters,
  placeholder,
  searchInputClassName,
}: ActionTableFrameProps) => {
  return (
    <Stack className={cn("p-s10 bg-[#252525]", className)} test-id="eHuA_1BS">
      {(headerSlot || title || onSearch || onFilter) && (
        <div
          className={cn(
            "flex flex-col md:flex-row gap-y-s10 md:gap-y-0 md:gap-x-s10 md:items-center md:justify-between mb-s10 pt-1",
            headerClassName,
          )}
          test-id="2UHFHLDK"
        >
          {title && (
            <Heading as="h4" className="text-white">
              {title}
            </Heading>
          )}

          {onSearch && (
            <div className="order-1 md:order-0">
              <SearchBar
                searchWithSpecialCharacters={searchWithSpecialCharacters}
                isLoading={isSearching}
                disabled={!onSearch || disabled}
                onSearch={onSearch}
                {...searchArgs}
                test-id="Sf-DyhId"
                placeholder={placeholder}
                searchInputClassName={searchInputClassName}
              />
            </div>
          )}

          {headerSlot}

          {onFilter && (
            <div className="order-2 md:order-0" test-id="nD6zTc4B">
              <Button
                variant="secondary-stroked"
                size="large"
                onClick={onFilter}
                isLoading={isFiltering}
                disabled={disabled}
              >
                <div className="flex items-center space-x-2" test-id="epEqHsbB">
                  <FilterIcon />
                  <span className="hidden sm:inline" test-id="ncerP1F5">
                    Filter
                  </span>
                </div>
              </Button>
            </div>
          )}
        </div>
      )}

      {children}
    </Stack>
  );
};

ActionTableFrame.Header = ({
  children,
  title,
  className,
}: {
  children: React.ReactNode;
  title?: string;
  className?: string;
}) => (
  <div
    className={cn(
      "flex flex-col md:flex-row space-y-s10 md:space-y-0 md:space-x-s10 md:items-center md:justify-between mb-s10",
      className,
    )}
  >
    {title && (
      <Heading as="h4" className="text-white whitespace-nowrap">
        {title}
      </Heading>
    )}
    {children}
  </div>
);

export default ActionTableFrame;
